import { createFileRoute, notFound } from "@tanstack/react-router";

import LandingNotFound from "@/components/landing/LandingNotFound";
import {
  CONTENT_CACHE_CONTROL,
  CONTENT_NOT_FOUND_CACHE_CONTROL,
} from "@/content/cachePolicy";
import { landingPageContent } from "@/content/landing/source";
import FaqDirectoryPage from "./frequently-asked-questions/-components/FaqDirectoryPage";

/** The path the CMS files this page's `landingPage` document under. */
const PATH = "/frequently-asked-questions";

/**
 * The FAQ directory, as its own route rather than through `/$landing`.
 *
 * Its words are the same CMS record every other landing page is read from, and
 * the route reads it the same way. What is different is the drawing: every
 * other root-level marketing page is a stack of blocks, which is what
 * `LandingBlocks` renders, and this one is a single filtered collection. The
 * published page draws a search field and a row of category chips over one
 * hundred rows, and the block model has no place to put either control, so the
 * page keeps a component of its own. Every word it draws is the record's,
 * chips included: `landingFaq` carries the chip row and each row's category.
 *
 * A static segment outranks `/$landing`, so this route answers the path. The
 * path stays in `content/landing/paths.ts` deliberately: `marketingHref` reads
 * that list to keep the footer's "FAQ" link inside the app, and the prerender
 * list is built from it.
 *
 * A sibling of `_app` for the reason `blog`, `academy`, `home` and `pricing`
 * are: the page is public and `_app`'s guard would bounce an anonymous reader
 * to sign-in.
 */
export const Route = createFileRoute("/frequently-asked-questions")({
  loader: async () => {
    const page = await landingPageContent({ data: PATH });
    if (!page) {
      throw notFound();
    }
    return page;
  },
  /** `loaderData` is absent exactly when the loader threw `notFound()`. */
  headers: ({ loaderData }) => ({
    "Cache-Control": loaderData
      ? CONTENT_CACHE_CONTROL
      : CONTENT_NOT_FOUND_CACHE_CONTROL,
  }),
  component: FaqDirectoryRoute,
  notFoundComponent: LandingNotFound,
});

function FaqDirectoryRoute() {
  return <FaqDirectoryPage page={Route.useLoaderData()} />;
}
